<script lang="ts">
import { defineComponent, ref, watch, nextTick } from 'vue'
import { ACTIVITIES } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import ExternalHtmlWrapper from '@/components/_core/ExternalHtmlWrapper.vue'
import ProgressIndicator from '@/components/loading/ProgressIndicator.vue'
import DateDefault from '@/components/date/DateDefault.vue'
import backdrop from '@/models/backdrop'
import wait from '@/utilities/wait'
import { dateFormatJa2 } from '@/utilities/formatter'
import type { Ref } from 'vue'

const BACKDROP_CANCEL_ID = 'activity'

interface AppWindow extends Window {
  ga: any
}

declare const window: AppWindow

export default defineComponent({
  name: 'ActivityForHeader',

  components: {
    ExternalHtmlWrapper,
    ProgressIndicator,
    DateDefault,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: null,
    },
  },

  emits: ['update:isOpen'],

  setup(props, { emit }) {
    // model の backdrop の処理実体を定義
    backdrop(() => {
      isOpenLocal.value = false
      emit('update:isOpen', isOpenLocal.value)
    }, BACKDROP_CANCEL_ID)

    // お知らせの description 部分の DOM 格納用
    const description = ref()

    const activities: Ref<Activity[]> = ref([])

    // お知らせドロップダウンの表示
    const isOpenLocal = ref(false)

    // お知らせのデータ取得処理中フラグ
    const isFetching = ref(false)

    // お知らせの次回取得ページ番号
    const page = ref(1)

    // 未読のアクティビティが存在するかどうか
    const hasUnreadActivity = ref(false)

    // 最終データまで読み込んでいるかどうか
    const isEndOfList = ref(false)

    watch(() => props.isOpen, () => {
      isOpenLocal.value = props.isOpen
    })

    /**
     * アクティビティの開閉状態を監視し、「閉じる」状態になった際に description のスクロール位置をリセットする
     */
    watch(isOpenLocal, async (newValue, oldValue) => {
      if (!newValue && oldValue) {
        if (description.value) {
          // アクティビティが閉じながらスクロール位置リセットしてしまうとガタつきが発生するため、閉じる際の CSS アニメーション中は待機する
          await wait(0.5)
          description.value.scrollTop = 0
        }
      }
    })

    const toggleActivity = () => {
      isOpenLocal.value = !isOpenLocal.value
      emit('update:isOpen', isOpenLocal.value)
    }

    const fetchActivities = async () => {
      if (page.value <= 0) return
      isFetching.value = true

      const params = {
        page: page.value,
      }

      try {
        await apiRequestManager.handleRequest({
          endpoint: ACTIVITIES.FETCH,
          params,
          callback: (response, isSuccess) => {
            if (isSuccess) {
              const $ = response.data as ActivityInfo

              // 既に activities 配列に存在する id のお知らせを排除した上で配列を生成する
              const newActivities = $.activities.filter(v => !activities.value.some(_ => _.id === v.id))

              newActivities.forEach(v => {
                activities.value.push({
                  id: v.id,
                  category: v.category,
                  image: v.thumb_image_url,
                  htmlDescription: v.body_html,
                  date: v.published_at,
                  to: v.url,
                  isRead: v.read,
                })
              })

              // 2 ページ以降の読み込みの場合は、読み込み完了時に新しく追加されたお知らせの位置までスクロールする
              if (page.value > 1) {
                nextTick(() => {
                  description.value.scrollTop = description.value.clientHeight
                })
              }

              // 新着データが 5 件未満の場合は最終データまで読み込み完了している
              if (newActivities.length < 5) isEndOfList.value = true

              // 未読お知らせ数トラッキング
              if ($.unread_activity_count > 0) trackEvent('unreadCount', $.unread_activity_count.toString())

              hasUnreadActivity.value = !$.all_read
              page.value++
            }

            isFetching.value = false
          }
        })
      } catch (error) {
        console.log(error)
      }
    }

    /**
     * お知らせをクリックしたときの既読処理
     * @param id 対象のお知らせ id
     * @param to 遷移先パス
     */
    const readActivity = async (id: number, category: string, to: string) => {
      const path = ACTIVITIES.READ.path({ id })
      const endpoint = { ...ACTIVITIES.READ, path }

      try {
        await apiRequestManager.handleRequest({
          endpoint,
          callback: (_, isSuccess) => {
            if (isSuccess) {
              trackEvent('read', category)
              if (to) location.assign(to)
            }
          }
        })
      } catch (error) {
        console.log(error)
      }
    }

    const readMore = () => {
      fetchActivities()
      trackEvent('readMore')
    }

    /**
     * お知らせをクリックした際の処理
     * @param item 対象のお知らせ情報
     */
    const navigateTo = (item: Activity) => {
      if (item.id) readActivity(item.id, item.category, item.to)
    }

    const trackEvent = (eventLabel: string, value?: string) => {
      if (typeof window.ga !== 'undefined') {
        window.ga(
          'send',
          'event',
          'activity',
          eventLabel,
          value,
          { nonInteraction: true },
        )
      }
    }

    // 初回取得
    fetchActivities()

    return {
      // constants
      BACKDROP_CANCEL_ID,

      // data
      description,
      activities,
      isOpenLocal,
      isFetching,
      isEndOfList,

      // computed
      hasUnreadActivity,

      // methods
      toggleActivity,
      fetchActivities,
      readMore,
      navigateTo,
      dateFormatJa2,
    }
  }
})
</script>

<template>
  <div
    class="l-unit__activityAction"
    :data-open="isOpenLocal"
    :data-backdrop-cancel="BACKDROP_CANCEL_ID">
    <div class="l-unit__activityAction--description">
      <div
        class="l-unit__activityWrapper"
        :data-empty="!activities.length">
        <div class="l-unit__activityWrapper--header">
          <div class="o-heading__activity">
            <h2 class="o-heading__activity--item">
              お知らせ
            </h2>
          <!-- /.o-heading__activity --></div>
        <!-- /.l-unit__activityWrapper header --></div>

        <div
          id="activityDescription"
          ref="description"
          class="l-unit__activityWrapper--description">
          <template v-if="activities.length">
            <div class="o-list__activity">
              <ul class="o-list__activity--items">
                <li
                  v-for="activity in activities"
                  :key="activity.id"
                  class="o-list__activity--item">
                  <div
                    class="l-unit__activity"
                    :data-unread="!activity.isRead">
                    <a
                      class="l-unit__activity--link"
                      @click.prevent="navigateTo(activity)">
                      <div
                        v-if="activity.image"
                        class="l-unit__activity--image">
                        <img
                          class="img"
                          :src="activity.image"
                          loading="lazy"
                          alt=""
                          data-is-liquid="true">
                      <!-- /.l-unit__activity image --></div>

                      <ExternalHtmlWrapper
                        :class-names="['l-unit__activity--description']"
                        :contents-html="activity.htmlDescription" />

                      <div class="l-unit__activity--footer is-date">
                        <DateDefault
                          :date="activity.date"
                          :format="dateFormatJa2(activity.date)" />
                      <!-- /.l-unit__activity footer --></div>
                    <!-- /.l-unit__activity link --></a>
                  <!-- /.l-unit__activity --></div>
                </li>
              </ul>
            <!-- /.o-list__activity --></div>

            <div
              v-if="isEndOfList"
              class="o-label__activityEnd">
              <p class="o-label__activityEnd--item">
                最近のお知らせは以上です
              </p>
            <!-- /.o-label__activityEnd --></div>
            <div
              v-else
              class="o-action__readMore">
              <button
                class="o-action__readMore--item"
                @click.prevent="readMore">
                もっと見る
              </button>
            <!-- /.o-action__readMore --></div>
          </template>

          <div
            v-else
            class="o-label__activityEnd">
            <p class="o-label__activityEnd--item">
              最近のお知らせはありません
            </p>
          <!-- /.o-label__activityEnd --></div>
        <!-- /.l-unit__activityWrapper description --></div>

        <div
          class="l-unit__activityWrapper--description is-loader"
          :data-screenlock="isFetching ? true : false">
          <ProgressIndicator size="small" />
        <!-- /.l-unit__activityWrapper description --></div>
      <!-- /.l-unit__activityWrapper --></div>
    <!-- /.l-unit__activityAction description --></div>

    <div class="l-unit__activityAction--action">
      <div
        class="o-action__activity u-action__tooltip"
        :data-unread="hasUnreadActivity"
        :data-active="isOpenLocal"
        data-tooltip="お知らせ">
        <!--
          backdrop クリックイベントでメニューを閉じるアクションを発火させるため、
          container store で処理をする。
          ボタンクリックで container store のイベントを発火させないため、stop propagation を駆動させる。
        -->
        <button
          class="o-action__activity--item"
          @click="toggleActivity">
          {{ isOpenLocal ? 'お知らせを閉じる' : 'お知らせを開く' }}
        </button>
      <!-- /.o-action__activity --></div>
    <!-- /.l-unit__activityAction action --></div>
  <!-- /.l-unit__activityAction --></div>
</template>

<style lang="sass" scoped>
// layout
.l-unit
  &__activityAction
    $root: &
    position: relative

    & &--description
      width: 400px
      position: absolute
      top: #{$header-primary-height-pc}px
      right: 0
      transform: translateY(-8px)
      background-color: rgba($bg-white, 1)
      border: 1px solid $border-x-light-gray
      border-radius: $border-radius
      box-shadow: $box-shadow
      visibility: hidden
      opacity: 0
      z-index: 1
      +transition

    &[data-open="true"]
      #{$root}--description
        transform: translateY(0)
        visibility: visible
        opacity: 1

    +breakpoint($max-media: tablet)
      #{$root}--description
        width: 100%
        height: 100%
        position: fixed
        top: #{$header-primary-height-sp}px
        border: none
        border-radius: 0
        box-shadow: none

  &__activityWrapper
    $root: &
    position: relative

    & &--header
      position: relative
      padding: 1em
      box-shadow: $drop-bottom-shadow
      z-index: 1

    & &--description
      max-height: 458px
      overflow: auto

      &.is-loader
        width: 100%
        height: 100%
        max-height: none
        display: flex
        flex-direction: column
        justify-content: center
        position: absolute
        top: 0
        left: 0
        overflow: hidden
        visibility: hidden
        backdrop-filter: blur(6px)
        background-color: rgba($bg-white, 0.3)
        opacity: 0
        z-index: 1
        +transition

        &[data-screenlock="true"]
          visibility: visible
          opacity: 1

    +breakpoint($max-media: tablet)
      &[data-empty="true"]
        border-bottom: 1px solid $border-x-light-gray

      #{$root}--header
        padding-left: changeSizePx2Em($font-normal-px + 12)

      #{$root}--description
        max-height: none

  &__activity
    $root: &
    $image-size: 48
    $padding: 12
    $image-margin-size: 16
    +base-layout(left, $root, $image-size, $image-size, $image-margin-size, $padding)
    line-height: 1

    & &--link
      display: flex
      flex-direction: column
      justify-content: center
      color: $font-black
      text-decoration: none
      +transition($duration: $transition-middle)

      +mouseover
        background-color: rgba($bg-x-light-gray, 1)

    & &--image
      > *
        vertical-align: top

    & &--description
      line-height: 1.5
      color: $font-light-gray
      font-size: $font-small

    & &--footer
      &.is-date
        margin-top: 0.25em

    &[data-unread="true"]
      $icon-size: 8

      #{$root}--link
        padding-right: #{$padding * 2 + $icon-size}px

        &::before
          content: ""
          width: #{$icon-size}px
          height: #{$icon-size}px
          display: block
          position: absolute
          top: 50%
          right: #{$padding}px
          transform: translateY(-50%)
          background-color: rgba($bg-light-red, 1)
          border-radius: 50%

      #{$root}--description
        color: $font-black

// object
.o-action
  &__activity
    $root: &
    $button-size: 40px
    line-height: 1

    & &--item
      width: $button-size
      height: $button-size
      position: relative
      overflow: hidden
      color: rgba($font-black, 0)
      font-size: $font-xx-small
      white-space: nowrap
      background:
        image: url(@/images/icon/icon-activity_02.svg)
        repeat: no-repeat
        position: center center

    &[data-active="true"]
      #{$root}--item
        background-image: url(@/images/icon/icon-activity_01.svg)

    &[data-unread="true"]
      #{$root}--item
        &::before
          content: "N"
          width: 1.6em
          line-height: 1.6
          display: block
          position: absolute
          top: -1px
          right: -1px
          color: $font-white
          font-size: $font-xx-small
          text-align: center
          background-color: rgba($bg-x-light-red, 1)
          border: 1px solid $border-white
          border-radius: 50%

    +breakpoint($max: false, $min: true, $min-media: tablet)
      #{$root}--item
        +mouseover
          background-image: url(@/images/icon/icon-activity_01.svg)

  &__readMore
    & &--item
      width: 100%
      display: block
      line-height: 1
      color: $font-dark-blue
      font-size: $font-small
      text-align: center
      padding: changeSizePx2Em($font-normal-px, $font-small-px)
      border-top: 1px solid $border-x-light-gray
      +transition($duration: $transition-middle)

      +mouseover
        text-decoration: underline
        background-color: rgba($bg-x-light-gray, 1)

.o-heading
  &__activity
    & &--item
      font-size: $font-small
      line-height: 1

.o-list
  &__activity
    & &--item
      border-top: 1px solid $border-x-light-gray

      &:first-child
        border-top: none

.o-label
  &__activityEnd
    & &--item
      line-height: 1
      color: $font-gray
      font-size: $font-small
      text-align: center
      padding: changeSizePx2Em($font-normal-px, $font-small-px)
      border-top: 1px solid $border-x-light-gray
</style>
